/**
 * Button State Contract Utilities
 * 
 * Enforces the Button State Contract across the application:
 * 1. Disable button while loading
 * 2. Show success or error feedback
 * 3. Refresh data on success
 * 4. No optimistic UI unless intentional
 * 
 * USAGE:
 * import { useActionState, ActionButton } from '../components/ui/action-button';
 * 
 * // Using the hook:
 * const { execute, isLoading, error } = useActionState(async () => {
 *   await api.delete(id);
 * }, { onSuccess: () => loadData(), successMessage: 'Deleted!' });
 * 
 * // Using the component:
 * <ActionButton 
 *   onClick={handleDelete}
 *   loadingText="Deleting..."
 *   successMessage="Deleted!"
 * >Delete</ActionButton>
 */

import React, { useState, useCallback } from 'react';
import { Button } from './button';
import { Loader2, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { normalizeError } from '../../lib/apiNormalizer';

/**
 * Action states for button feedback
 */
export const ActionState = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error'
};

/**
 * Hook for managing action state with proper feedback
 * 
 * @param {Function} action - Async function to execute
 * @param {Object} options - Configuration options
 * @returns {Object} - State and execute function
 * 
 * @example
 * const { execute, isLoading, state } = useActionState(
 *   async () => await api.deleteItem(id),
 *   { 
 *     onSuccess: () => { loadItems(); navigate('/items'); },
 *     successMessage: 'Item deleted successfully',
 *     errorMessage: 'Failed to delete item'
 *   }
 * );
 */
export function useActionState(action, options = {}) {
  const {
    onSuccess,
    onError,
    successMessage,
    errorMessage = 'Action failed',
    showSuccessToast = true,
    showErrorToast = true,
    resetDelay = 2000, // Reset state after success/error
  } = options;

  const [state, setState] = useState(ActionState.IDLE);
  const [error, setError] = useState(null);

  const execute = useCallback(async (...args) => {
    // Prevent double execution
    if (state === ActionState.LOADING) return;

    setState(ActionState.LOADING);
    setError(null);

    try {
      const result = await action(...args);
      
      setState(ActionState.SUCCESS);
      
      if (showSuccessToast && successMessage) {
        toast.success(successMessage);
      }
      
      // Execute success callback (refresh data, navigate, etc.)
      if (onSuccess) {
        await onSuccess(result);
      }

      // Reset to idle after delay
      if (resetDelay > 0) {
        setTimeout(() => setState(ActionState.IDLE), resetDelay);
      }

      return { success: true, data: result };
    } catch (err) {
      const errorMsg = normalizeError(err, errorMessage);
      
      setState(ActionState.ERROR);
      setError(errorMsg);
      
      if (showErrorToast) {
        toast.error(errorMsg);
      }
      
      if (onError) {
        onError(err);
      }

      // Reset to idle after delay
      if (resetDelay > 0) {
        setTimeout(() => {
          setState(ActionState.IDLE);
          setError(null);
        }, resetDelay);
      }

      return { success: false, error: errorMsg };
    }
  }, [action, onSuccess, onError, successMessage, errorMessage, showSuccessToast, showErrorToast, resetDelay, state]);

  const reset = useCallback(() => {
    setState(ActionState.IDLE);
    setError(null);
  }, []);

  return {
    execute,
    state,
    error,
    reset,
    isLoading: state === ActionState.LOADING,
    isSuccess: state === ActionState.SUCCESS,
    isError: state === ActionState.ERROR,
    isIdle: state === ActionState.IDLE,
  };
}

/**
 * Action Button Component with built-in state management
 * 
 * @example
 * <ActionButton
 *   action={async () => await api.save(data)}
 *   onSuccess={() => loadData()}
 *   successMessage="Saved!"
 *   loadingText="Saving..."
 * >
 *   Save
 * </ActionButton>
 */
export function ActionButton({
  children,
  action,
  onSuccess,
  onError,
  successMessage,
  errorMessage,
  loadingText,
  successText,
  showSuccessIcon = true,
  showErrorIcon = true,
  disabled = false,
  variant = 'default',
  size = 'default',
  className = '',
  'data-testid': testId,
  ...props
}) {
  const { execute, state, isLoading } = useActionState(action, {
    onSuccess,
    onError,
    successMessage,
    errorMessage,
  });

  const handleClick = async (e) => {
    e.preventDefault();
    await execute();
  };

  // Determine button content based on state
  const renderContent = () => {
    switch (state) {
      case ActionState.LOADING:
        return (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            {loadingText || children}
          </>
        );
      case ActionState.SUCCESS:
        return (
          <>
            {showSuccessIcon && <Check className="h-4 w-4 mr-2" />}
            {successText || children}
          </>
        );
      case ActionState.ERROR:
        return (
          <>
            {showErrorIcon && <AlertCircle className="h-4 w-4 mr-2" />}
            {children}
          </>
        );
      default:
        return children;
    }
  };

  // Determine variant based on state
  const getVariant = () => {
    if (state === ActionState.SUCCESS) return 'success';
    if (state === ActionState.ERROR) return 'destructive';
    return variant;
  };

  return (
    <Button
      onClick={handleClick}
      disabled={disabled || isLoading}
      variant={getVariant()}
      size={size}
      className={className}
      data-testid={testId}
      data-state={state}
      {...props}
    >
      {renderContent()}
    </Button>
  );
}

/**
 * Submit Button - Specialized for form submissions
 * Automatically prevents double submission
 */
export function SubmitButton({
  children = 'Submit',
  isSubmitting = false,
  loadingText = 'Submitting...',
  disabled = false,
  variant = 'default',
  size = 'default',
  className = '',
  type = 'submit',
  'data-testid': testId,
  ...props
}) {
  return (
    <Button
      type={type}
      disabled={disabled || isSubmitting}
      variant={variant}
      size={size}
      className={className}
      data-testid={testId}
      {...props}
    >
      {isSubmitting ? (
        <>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          {loadingText}
        </>
      ) : (
        children
      )}
    </Button>
  );
}

/**
 * Delete Button with confirmation
 * Shows loading state and handles errors
 */
export function DeleteButton({
  onDelete,
  onSuccess,
  itemName = 'item',
  confirmMessage,
  successMessage,
  disabled = false,
  size = 'sm',
  className = '',
  'data-testid': testId,
  children,
  ...props
}) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    const message = confirmMessage || `Are you sure you want to delete this ${itemName}? This action cannot be undone.`;
    
    if (!window.confirm(message)) return;

    setIsDeleting(true);
    
    try {
      await onDelete();
      toast.success(successMessage || `${itemName} deleted successfully`);
      if (onSuccess) await onSuccess();
    } catch (error) {
      toast.error(normalizeError(error, `Failed to delete ${itemName}`));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Button
      variant="destructive"
      size={size}
      onClick={handleDelete}
      disabled={disabled || isDeleting}
      className={className}
      data-testid={testId}
      {...props}
    >
      {isDeleting ? (
        <>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Deleting...
        </>
      ) : (
        children || 'Delete'
      )}
    </Button>
  );
}

/**
 * Save Button with loading state
 */
export function SaveButton({
  onSave,
  onSuccess,
  isLoading = false,
  disabled = false,
  loadingText = 'Saving...',
  successMessage = 'Saved successfully',
  size = 'default',
  className = '',
  'data-testid': testId,
  children = 'Save',
  ...props
}) {
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (isSaving) return;
    
    setIsSaving(true);
    
    try {
      await onSave();
      toast.success(successMessage);
      if (onSuccess) await onSuccess();
    } catch (error) {
      toast.error(normalizeError(error, 'Failed to save'));
    } finally {
      setIsSaving(false);
    }
  };

  const loading = isLoading || isSaving;

  return (
    <Button
      onClick={handleSave}
      disabled={disabled || loading}
      size={size}
      className={className}
      data-testid={testId}
      {...props}
    >
      {loading ? (
        <>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          {loadingText}
        </>
      ) : (
        children
      )}
    </Button>
  );
}

/**
 * Higher-order function to wrap any async action with loading state
 * 
 * @example
 * const [handleSave, isSaving] = useAsyncAction(async (data) => {
 *   await api.save(data);
 *   loadData();
 * }, 'Saved!', 'Failed to save');
 */
export function useAsyncAction(action, successMessage = null, errorMessage = 'Action failed') {
  const [isLoading, setIsLoading] = useState(false);

  const execute = useCallback(async (...args) => {
    if (isLoading) return { success: false };
    
    setIsLoading(true);
    
    try {
      const result = await action(...args);
      if (successMessage) {
        toast.success(successMessage);
      }
      return { success: true, data: result };
    } catch (error) {
      toast.error(normalizeError(error, errorMessage));
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  }, [action, isLoading, successMessage, errorMessage]);

  return [execute, isLoading];
}

/**
 * Form submission handler with loading state
 * Prevents double submission automatically
 * 
 * @example
 * const { handleSubmit, isSubmitting } = useFormSubmit(
 *   async (data) => await api.create(data),
 *   { onSuccess: () => navigate('/list'), successMessage: 'Created!' }
 * );
 * 
 * <form onSubmit={handleSubmit}>
 *   <SubmitButton isSubmitting={isSubmitting}>Create</SubmitButton>
 * </form>
 */
export function useFormSubmit(submitAction, options = {}) {
  const {
    onSuccess,
    onError,
    successMessage,
    errorMessage = 'Submission failed',
    resetForm,
  } = options;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleSubmit = useCallback(async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await submitAction(e);
      
      if (successMessage) {
        toast.success(successMessage);
      }
      
      if (resetForm) {
        resetForm();
      }
      
      if (onSuccess) {
        await onSuccess(result);
      }

      return { success: true, data: result };
    } catch (error) {
      const errorMsg = normalizeError(error, errorMessage);
      setSubmitError(errorMsg);
      toast.error(errorMsg);
      
      if (onError) {
        onError(error);
      }

      return { success: false, error: errorMsg };
    } finally {
      setIsSubmitting(false);
    }
  }, [submitAction, isSubmitting, onSuccess, onError, successMessage, errorMessage, resetForm]);

  const resetError = useCallback(() => setSubmitError(null), []);

  return {
    handleSubmit,
    isSubmitting,
    submitError,
    resetError,
  };
}

export default {
  ActionState,
  useActionState,
  ActionButton,
  SubmitButton,
  DeleteButton,
  SaveButton,
  useAsyncAction,
  useFormSubmit,
};
